/**
 * The coloured date block on a `WorkshopList` card.
 *
 * A caller of the shared `DateBadge` primitive rather than its own second
 * implementation — the colour is the sub-region's accent, which is what makes
 * a mixed list readable by territory at a glance, and it's an arbitrary hex,
 * which is exactly why `DateBadge` grew a `color` override.
 *
 * Sizing is the badge's own business: `stretched` carries the list's scale
 * (width, numeral, labels, corner), so this passes `size` straight through
 * rather than measuring the block itself.
 */
import { formatVenueDate } from '../../../utils/venueTime';

import { DateBadge } from './DateBadge';
import type { WorkshopListSize } from './workshopListSizes';

export type WorkshopListDateField = 'weekday' | 'day' | 'month';

const ALL_DATE_FIELDS: WorkshopListDateField[] = ['weekday', 'day', 'month'];

export interface WorkshopListDateBlockProps {
  /** Venue-local wall clock — never re-derived from the instant. */
  local: string;
  color: string;
  size?: WorkshopListSize;
  /** Which of the three lines render. Default all three. */
  dateFields?: WorkshopListDateField[];
}

export function WorkshopListDateBlock({
  local,
  color,
  size = 'md',
  dateFields = ALL_DATE_FIELDS,
}: WorkshopListDateBlockProps) {
  return (
    <DateBadge
      aria-hidden="true"
      weekday={dateFields.includes('weekday') ? formatVenueDate(local, 'EEE') : undefined}
      day={dateFields.includes('day') ? formatVenueDate(local, 'd') : ''}
      month={dateFields.includes('month') ? formatVenueDate(local, 'MMM') : undefined}
      color={color}
      size={size}
      stretched
    />
  );
}
